import { siteConfig } from '@/config/site'
import React, { useEffect } from 'react'
import {
  Outlet,
  Navigate,
  useSearchParams,
  useNavigate,
  useLocation,
} from 'react-router-dom'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

interface PrivateRouteProps {
  localAuth: {
    sessionKey: string
  }
}

interface IAuth {
  isDemo: boolean
  firstname: string
  lastname: string
  email: string
  tier: string
  isFirstLogin: boolean
  isTutorial: boolean
  sessionKey: string
  isAuthenticated: boolean
  setAuth: (auth: string) => void
  resetAuth: () => void
}

export function authHelper({ auth }: { auth: string }): Partial<IAuth> {
  const parsedAuth = JSON.parse(auth ?? '{}')
  const sessionKey = parsedAuth['sessionKey'] ?? ''
  const isDemo = sessionKey === 'a6cf9f6e-3341-4996-8d6a-83e826b67adf'

  return {
    isDemo: isDemo,
    firstname: parsedAuth['firstname'] ?? '',
    lastname: parsedAuth['lastname'] ?? '',
    email: parsedAuth['email'] ?? '',
    tier: parsedAuth['tier'] ?? '',
    sessionKey: sessionKey,
    isFirstLogin: parsedAuth['firstLogin'],
    isTutorial: parsedAuth['tutorial'],
    isAuthenticated: Boolean(sessionKey),
  }
}

export const useAuthStore = create<IAuth>()(
  devtools(
    (set) => {
      const auth = localStorage.getItem('auth') ?? '{}'
      return {
        ...authHelper({ auth: auth }),
        setAuth: (auth: string) => {
          set(() => {
            return {
              ...authHelper({ auth: auth }),
            }
          })
        },
        resetAuth: () => {
          set(() => {
            return {
              isDemo: false,
              firstname: '',
              lastname: '',
              email: '',
              tier: '',
              sessionKey: '',
              isFirstLogin: false,
              isTutorial: false,
              isAuthenticated: false,
            }
          })
        },
      }
    },
    { name: 'AuthStore', enabled: process.env.NODE_ENV === 'development' }
  )
)

const PrivateRoute: React.FC<PrivateRouteProps> = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const locations = searchParams.get('locations')
  const auth = localStorage.getItem('auth') ?? '{}'
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const location = useLocation()
  const [isCheckingAuth, setIsCheckingAuth] = React.useState(true)
  const pathname = location.pathname

  useEffect(() => {
    useAuthStore.getState().setAuth(auth)
    setIsCheckingAuth(false)

    return () => {
      useAuthStore.getState().resetAuth()
    }
  }, [JSON.stringify(auth)])

  useEffect(() => {
    if (isAuthenticated) {
      navigate(`${pathname}${location.search}`)
    }
    if (!isAuthenticated && !isCheckingAuth) {
      if (locations) {
        navigate(`/sign-in?locations=${locations}`)
      } else {
        window.location.replace('/sign-in')
      }
    }
  }, [isAuthenticated, isCheckingAuth])

  if (isAuthenticated) {
    return <Outlet />
  }
}

export default PrivateRoute
